'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { inventoryApi, partsApi, alertsApi } from '@parts/api';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  TrendingUp,
  TrendingDown,
  Package,
  AlertTriangle,
  Search,
  Filter,
  Download,
  ArrowLeft,
} from 'lucide-react';

export default function InventoryManagementPage() {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    operationType: '',
    station: '',
    startDate: '',
    endDate: '',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadData();
  }, [page, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, alertsRes, logsRes] = await Promise.all([
        inventoryApi.getStats(filters),
        alertsApi.list({ status: 'PENDING', limit: 5 }),
        inventoryApi.getLogs({
          ...filters,
          page,
          limit: 20,
        }),
      ]);
      setStats(statsRes);
      setAlerts(alertsRes.items);
      setLogs(logsRes.items);
      setTotalPages(Math.ceil(logsRes.total / 20));
    } catch (error) {
      console.error('Failed to load inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOperationIcon = (type: string) => {
    switch (type) {
      case 'CHECK_IN':
      case 'RECEIVING':
      case 'RETURN':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'CHECK_OUT':
      case 'SCRAP':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Package className="w-4 h-4 text-blue-600" />;
    }
  };

  const getOperationColor = (type: string) => {
    switch (type) {
      case 'CHECK_IN':
      case 'RECEIVING':
      case 'RETURN':
        return 'text-green-700 bg-green-50';
      case 'CHECK_OUT':
      case 'SCRAP':
        return 'text-red-700 bg-red-50';
      default:
        return 'text-blue-700 bg-blue-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/parts')}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {t.inventory.title}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {t.inventory.stats}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('/api/inventory/logs?export=true', '_blank')}
              >
                <Download className="w-4 h-4 mr-2" />
                {t.common.export}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t.inventory.totalOperations}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalOperations || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t.inventory.totalCheckIns}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.checkIns || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingDown className="h-8 w-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t.inventory.totalCheckOuts}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.checkOuts || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">
                    {t.inventory.totalAdjustments}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.adjustments || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Logs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      placeholder={t.common.search}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Button variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  {t.common.filter}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  placeholder={t.common.startDate}
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
                <Input
                  type="date"
                  placeholder={t.common.endDate}
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>
            </div>

            {/* Inventory Logs */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t.inventory.logs}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.common.time}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.parts.partNumber}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.inventory.operationType}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.inventory.quantity}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.parts.station}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t.inventory.operator}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          {t.common.loading}
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          {t.common.noData}
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {log.part?.partNumber}
                            </div>
                            <div className="text-xs text-gray-500">
                              {log.part && (locale === 'zh' ? (log.part.partNameCn || log.part.partNameEn) : log.part.partNameEn)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOperationColor(
                                log.operationType,
                              )}`}
                            >
                              {getOperationIcon(log.operationType)}
                              <span className="ml-1">
                                {log.operationType === 'INBOUND' ? t.inventory.inbound : 
                                 log.operationType === 'OUTBOUND' ? t.inventory.outbound :
                                 log.operationType === 'ADJUST' ? t.inventory.adjust :
                                 log.operationType === 'TRANSFER' ? t.inventory.transfer :
                                 log.operationType}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {log.quantity > 0 ? '+' : ''}
                              {log.quantity}
                            </div>
                            <div className="text-xs text-gray-500">
                              {log.previousStock} → {log.newStock}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.station || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.operatorName}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      {t.common.previous}
                    </Button>
                    <span className="text-sm text-gray-700 flex items-center">
                      {t.common.page} {page} {t.common.of} {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      {t.common.next}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Alerts */}
          <div className="space-y-6">
            {/* Stock Alerts */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t.alerts.title}
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => router.push('/alerts')}
                >
                  {t.common.viewAll}
                </Button>
              </div>

              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">{t.common.noAlerts}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-3 rounded-lg border ${
                        alert.severity === 'CRITICAL'
                          ? 'bg-red-50 border-red-200'
                          : alert.severity === 'HIGH'
                          ? 'bg-orange-50 border-orange-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <AlertTriangle
                          className={`w-5 h-5 mt-0.5 mr-2 ${
                            alert.severity === 'CRITICAL'
                              ? 'text-red-600'
                              : alert.severity === 'HIGH'
                              ? 'text-orange-600'
                              : 'text-yellow-600'
                          }`}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {alert.part && (locale === 'zh' ? (alert.part.partNameCn || alert.part.partNameEn) : alert.part.partNameEn)}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">{alert.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t.common.quickActions}
              </h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => router.push('/parts/scan?mode=checkin')}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t.inventory.checkIn}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => router.push('/parts/scan?mode=checkout')}
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  {t.inventory.checkOut}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

